import { useTranslate } from '@ur-solutions/react-hooks'
import { Checkbox } from '@/components_deprecated'
import type { FilterChildren } from '@/components_deprecated/FilterPopup'
import { FilterPopup } from '@/components_deprecated/FilterPopup'
import {
  FilterCheckmarksWrapper,
  Status,
  StatusButton,
  StyledScrollBar,
} from '@/components_deprecated/FilterPopup/components'
import React from 'react'
import type { InfringementCategoryIdentifiers } from '../consts'
import { InfringementSeverities } from '../consts'
import type { InfringementsTableFilter, SeverityCategory } from '../types'
import {
  useInfringementSeverityFunctions,
  useInfringementCategoryFunctions,
} from '../util'
import styled from 'styled-components'

const StyledFilterCheckbox = styled(Checkbox)`
  input:disabled + .--checkbox-box {
    border-color: ${props => props.theme.colors.gray8};
    color: ${props => props.theme.colors.gray8};
    opacity: 0.55;
    filter: grayscale(1);
  }
`

export const InfringementPopupFilter: React.FC<
  FilterChildren<InfringementsTableFilter>
> = ({ filter, onUpdate }) => {
  const translations = useTranslate({
    archived: 'common.archived',
    status: 'common.status',
    open: 'common.open-alt',
    closed: 'common.closed',

    severities: 'infringements.severities',
    categories: 'common.categories',

    minorInfringement: 'infringements.minor-infringement',
    seriousInfringement: 'infringements.serious-infringement',
    verySeriousInfringement: 'infringements.very-serious-infringement',
    mostSeriousInfringement: 'infringements.most-serious-infringement',
    infringementPoints: ['infringements.n-points', { n: 0 }],
  })
  const severities = Object.keys(InfringementSeverities) as SeverityCategory[]
  const categoryIdentifiers: InfringementCategoryIdentifiers[] = ['B', 'C', 'D']

  const selectedSeveritiesCount =
    severities.length - filter.excludeSeverities.length
  const excludedCategoryIdentifiers = new Set(
    filter.excludeCategories.map(category => category[0])
  )
  const selectedCategoriesCount =
    categoryIdentifiers.length - excludedCategoryIdentifiers.size

  function toggleArchived(archived: boolean) {
    return () =>
      onUpdate({
        archived,
      })
  }

  function toggleSeverity(severity: SeverityCategory) {
    return () => {
      const isExcluded = filter.excludeSeverities.includes(severity)

      if (isExcluded) {
        onUpdate({
          excludeSeverities: filter.excludeSeverities.filter(
            s => s !== severity
          ),
        })
        return
      }

      if (filter.excludeSeverities.length >= severities.length - 1) {
        return
      }

      onUpdate({
        excludeSeverities: [...filter.excludeSeverities, severity],
      })
    }
  }

  function toggleCategories(category: InfringementCategoryIdentifiers) {
    return () => {
      const excludedIdentifiers = new Set(
        filter.excludeCategories.map(currentCategory => currentCategory[0])
      )
      const isExcluded = excludedIdentifiers.has(category)

      if (isExcluded) {
        onUpdate({
          excludeCategories: filter.excludeCategories.filter(
            currentCategory => currentCategory[0] !== category
          ),
        })
        return
      }

      if (excludedIdentifiers.size >= categoryIdentifiers.length - 1) {
        return
      }

      onUpdate({
        excludeCategories: [
          ...filter.excludeCategories,
          ...getCategoriesFromIdentifier(category),
        ],
      })
    }
  }

  const { getSeverityTranslation, getSeverityValue } =
    useInfringementSeverityFunctions()

  const { getCategoryTranslation, getCategoriesFromIdentifier } =
    useInfringementCategoryFunctions()

  return (
    <FilterPopup>
      <h4>{translations.status}</h4>
      <Status statusCount={2}>
        <StatusButton
          active={filter.archived === false}
          onClick={toggleArchived(false)}
        >
          {translations.open}/{translations.closed}
        </StatusButton>
        <StatusButton
          active={filter.archived === true}
          onClick={toggleArchived(true)}
        >
          {translations.archived}
        </StatusButton>
      </Status>

      <h4>{translations.severities}</h4>

      <FilterCheckmarksWrapper>
        <StyledScrollBar>
          {severities.map(severity => {
            const checked = !filter.excludeSeverities.includes(severity)
            const disabled = checked && selectedSeveritiesCount === 1
            const label = `${getSeverityTranslation(
              severity
            )} (${translations.infringementPoints({
              n: getSeverityValue(severity),
            })})`
            return (
              <StyledFilterCheckbox
                key={severity}
                checked={checked}
                disabled={disabled}
                grayed={disabled}
                label={label}
                boxWidth="24px"
                boxHeight="24px"
                fontSize="0.8rem"
                onChange={toggleSeverity(severity)}
              />
            )
          })}
        </StyledScrollBar>
      </FilterCheckmarksWrapper>

      <h4>{translations.categories}</h4>

      <FilterCheckmarksWrapper>
        <StyledScrollBar>
          {categoryIdentifiers.map(categoryIdentifier => {
            const checked = !excludedCategoryIdentifiers.has(categoryIdentifier)
            const disabled = checked && selectedCategoriesCount === 1
            const label = getCategoryTranslation(categoryIdentifier)

            return (
              <StyledFilterCheckbox
                key={categoryIdentifier}
                checked={checked}
                disabled={disabled}
                grayed={disabled}
                label={label}
                boxWidth="24px"
                boxHeight="24px"
                fontSize="0.8rem"
                onChange={toggleCategories(categoryIdentifier)}
              />
            )
          })}
        </StyledScrollBar>
      </FilterCheckmarksWrapper>
    </FilterPopup>
  )
}
